import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { FormField, Knowledge, SelectOptions, CheckboxWithInputOptions } from '../../types/form';
import { Plus, X } from 'lucide-react';

interface OptionsFieldEditorProps {
  field: FormField;
  editedField: FormField;
  onFieldChange: (updates: Partial<FormField>) => void;
  knowledge: Knowledge[];
}

export const OptionsFieldEditor: React.FC<OptionsFieldEditorProps> = ({
  field,
  editedField,
  onFieldChange,
  knowledge,
}) => {
  const updateOptions = (updates: any) => {
    // If updating options array directly
    if (Array.isArray(updates)) {
        if (Array.isArray(editedField.options)) {
            onFieldChange({ options: updates });
        } else {
            onFieldChange({
                options: {
                    ...editedField.options,
                    optionsList: updates
                }
            });
        }
        return;
    }

    // If updating specific properties
    onFieldChange(updates);
  };

  const handleKnowledgeSelect = (value: string) => {
    if (value === 'custom') {
      if (field.field_type === 'checkbox_with_input') {
        const options = editedField.options as CheckboxWithInputOptions;
        onFieldChange({
          knowledge: undefined,
          knowledge_name: undefined,
          options: {
            ...editedField.options,
            inputType: options?.inputType || 'text',
            inputPlaceholder: options?.inputPlaceholder || 'Enter value',
            customOptions: []
          }
        });
      } else {
        onFieldChange({
          knowledge: undefined,
          knowledge_name: undefined,
          options: []
        });
      }
      return;
    }

    const selectedKnowledge = knowledge.find(k => k.id === value);
    
    // If knowledge was deleted while field was using it, reset to custom
    if (!selectedKnowledge && editedField.knowledge === value) {
      handleKnowledgeSelect('custom');
      return;
    }
    
    if (selectedKnowledge) {
      const optionsArray = Object.values(selectedKnowledge.options);
      
      if (field.field_type === 'checkbox_with_input') {
        const options = editedField.options as CheckboxWithInputOptions;
        onFieldChange({
          knowledge: value,
          knowledge_name: selectedKnowledge.name,
          options: {
            ...selectedKnowledge.options,
            inputType: options?.inputType || 'text',
            inputPlaceholder: options?.inputPlaceholder || 'Enter value'
          }
        });
      } else {
        onFieldChange({
          knowledge: value,
          knowledge_name: selectedKnowledge.name,
          options: optionsArray
        });
      }
    }
  };

  const getCurrentOptions = () => {
    if (field.field_type === 'checkbox_with_input') {
        const options = editedField.options as CheckboxWithInputOptions;
        return options?.customOptions || [];
    }
    const options = editedField.options as SelectOptions;
    return Array.isArray(editedField.options) 
        ? editedField.options 
        : (options?.optionsList || []);
  };

  const currentOptions = getCurrentOptions();

  return (
    <div className="space-y-4">
      {(field.field_type === 'radio' || field.field_type === 'checkbox') && (
        <div className="space-y-3">
          <div>
            <Label>Layout</Label>
            <Select
              value={editedField.layout_config?.layout || 'vertical'}
              onValueChange={(value) => {
                onFieldChange({
                  layout_config: {
                    ...editedField.layout_config,
                    layout: value as 'vertical' | 'horizontal'
                  }
                });
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vertical">Vertical (Stack)</SelectItem>
                <SelectItem value="horizontal">Horizontal (Inline)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {editedField.layout_config?.layout === 'horizontal' && (
            <div className="space-y-2 pl-4 border-l-2 border-muted">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`labelBelow-${field.id}`}
                  checked={editedField.layout_config?.labelBelow || false}
                  onCheckedChange={(checked) => {
                    onFieldChange({
                      layout_config: {
                        ...editedField.layout_config,
                        labelBelow: checked === true
                      }
                    });
                  }}
                />
                <Label htmlFor={`labelBelow-${field.id}`} className="text-sm font-normal">
                  Label below button
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`evenlySpaced-${field.id}`}
                  checked={editedField.layout_config?.evenlySpaced || false}
                  onCheckedChange={(checked) => {
                    onFieldChange({
                      layout_config: {
                        ...editedField.layout_config,
                        evenlySpaced: checked === true
                      }
                    });
                  }}
                />
                <Label htmlFor={`evenlySpaced-${field.id}`} className="text-sm font-normal">
                  Evenly spaced
                </Label>
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <Label>Use Shared Knowledge</Label>
        <Select
          value={editedField.knowledge || 'custom'}
          onValueChange={handleKnowledgeSelect}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select knowledge or create custom options" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="custom">Custom Options</SelectItem>
            {knowledge?.map(k => (
              <SelectItem key={k.id} value={k.id}>
                {k.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!editedField.knowledge && (
        <div>
          <Label>Custom Options</Label>
          <div className="space-y-2">
            {currentOptions.map((option: string, index: number) => (
              <div key={index} className="flex items-center space-x-2">
                <Input
                  value={option}
                  onChange={(e) => {
                    const newOptions = [...currentOptions];
                    newOptions[index] = e.target.value;
                    
                    if (field.field_type === 'checkbox_with_input') {
                        onFieldChange({
                            options: {
                                ...editedField.options,
                                customOptions: newOptions
                            }
                        });
                    } else {
                        updateOptions(newOptions);
                    }
                  }}
                  placeholder={`Option ${index + 1}`}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const newOptions = currentOptions.filter((_: string, i: number) => i !== index);
                    
                    if (field.field_type === 'checkbox_with_input') {
                        onFieldChange({
                            options: {
                                ...editedField.options,
                                customOptions: newOptions
                            }
                        });
                    } else {
                        updateOptions(newOptions);
                    }
                  }}
                  className="h-8 w-8 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const newOptions = [...currentOptions, ''];
                
                if (field.field_type === 'checkbox_with_input') {
                    onFieldChange({
                        options: {
                            ...editedField.options,
                            customOptions: newOptions
                        }
                    });
                } else {
                    updateOptions(newOptions);
                }
              }}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Option
            </Button>
          </div>
        </div>
      )}

      {editedField.knowledge && (
        <div>
          <Label>Knowledge Options</Label>
          <div className="space-y-1">
            {Object.entries(editedField.options as { [key: string]: string }).map(([key, value]) => (
              <div key={key} className="text-sm text-muted-foreground">
                {value}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
